import React, { useState, useEffect } from 'react';
import { Plus, Download, Trash2, Eye, ShoppingCart, Sparkles, Upload as UploadIcon, Palette, Loader, X, ZoomIn } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import CustomerLabelUpload from './CustomerLabelUpload';
import LabelGeneratorAI from './LabelGeneratorAI';
import CanvaTemplateGallery from './CanvaTemplateGallery';
import { useNavigate } from 'react-router-dom';

const MyLabels = () => {
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeTab, setActiveTab] = useState('upload');
  const [labelImage, setLabelImage] = useState(null);
  const [bottlePreview, setBottlePreview] = useState(null);
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [labelName, setLabelName] = useState('');
  const [labelDescription, setLabelDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [zoomedImage, setZoomedImage] = useState(null);

  const { token } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    loadLabels();
  }, []);

  const loadLabels = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/user-labels/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setLabels(data);
      }
    } catch (error) {
      console.error('Error loading labels:', error);
      toast({
        title: "Errore",
        description: "Impossibile caricare le etichette",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${backendUrl}/api/upload/serve/${path}`;
  };

  const generateBottlePreview = async (file) => {
    setGeneratingPreview(true);
    setBottlePreview(null);

    try {
      const formData = new FormData();
      
      let fileToUpload = file;
      if (typeof file === 'string') {
        const response = await fetch(file);
        const blob = await response.blob();
        fileToUpload = new File([blob], 'label.png', { type: blob.type });
      }
      
      formData.append('label_file', fileToUpload);
      
      const response = await fetch(`${backendUrl}/api/bottles/generate-preview`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Errore nella generazione dell\'anteprima');
      }
      
      const blob = await response.blob();
      
      // Salva il blob su server
      const saveFormData = new FormData();
      const previewFile = new File([blob], `preview-${Date.now()}.png`, { type: 'image/png' });
      saveFormData.append('file', previewFile);
      
      const saveResponse = await fetch(`${backendUrl}/api/upload/builder/label`, {
        method: 'POST',
        body: saveFormData
      });
      
      const saveResult = await saveResponse.json();
      
      if (saveResponse.ok && saveResult.success) {
        setBottlePreview(saveResult.filePath);
        
        toast({
          title: "Anteprima Generata!",
          description: "La tua bottiglia 3D è pronta",
        });
      } else {
        throw new Error('Errore nel salvataggio dell\'anteprima');
      }
      
    } catch (error) {
      console.error('Error generating preview:', error);
      toast({
        title: "Errore",
        description: "Impossibile generare l'anteprima",
        variant: "destructive"
      });
    } finally {
      setGeneratingPreview(false);
    }
  };

  const handleSaveLabel = async () => {
    if (!labelImage) {
      toast({
        title: "Errore",
        description: "Carica o genera un'etichetta prima di salvare",
        variant: "destructive"
      });
      return;
    }

    if (!labelName.trim()) {
      toast({
        title: "Errore",
        description: "Inserisci un nome per l'etichetta",
        variant: "destructive"
      });
      return;
    }

    setSaving(true);

    try {
      const labelData = {
        name: labelName,
        description: labelDescription || null,
        labelImagePath: labelImage,
        bottlePreviewPath: bottlePreview || null,
        sourceType: activeTab === 'upload' ? 'upload' : activeTab === 'ai' ? 'ai_generated' : 'canva',
        sourceMetadata: {}
      };

      const response = await fetch(`${backendUrl}/api/user-labels/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(labelData)
      });

      const result = await response.json();

      if (response.ok) {
        toast({
          title: "Successo!",
          description: "Etichetta salvata nella tua libreria"
        });
        
        setShowCreateModal(false);
        resetCreateForm();
        loadLabels();
      } else {
        throw new Error(result.message || 'Errore nel salvataggio');
      }

    } catch (error) {
      console.error('Save error:', error);
      toast({
        title: "Errore",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLabel = async (labelId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa etichetta?')) return;

    try {
      const response = await fetch(`${backendUrl}/api/user-labels/${labelId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();

      if (response.ok) {
        toast({
          title: "Successo",
          description: "Etichetta eliminata"
        });
        loadLabels();
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      toast({
        title: "Errore",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleUseForOrder = (label) => {
    // Store label in sessionStorage for use in Builder
    sessionStorage.setItem('selectedLabel', JSON.stringify({
      labelImage: label.labelImagePath,
      bottlePreview: label.bottlePreviewPath,
      name: label.name
    }));
    
    navigate('/builder');
    
    toast({
      title: "Etichetta Selezionata",
      description: "Vai al Builder per completare l'ordine"
    });
  };

  const resetCreateForm = () => {
    setLabelImage(null);
    setBottlePreview(null);
    setLabelName('');
    setLabelDescription('');
    setActiveTab('upload');
  };

  const handleAILabelGenerated = (filePath) => {
    setLabelImage(filePath);
    
    const imageUrl = `${backendUrl}/api/upload/serve/${filePath}`;
    
    fetch(imageUrl)
      .then(res => res.blob())
      .then(blob => {
        const file = new File([blob], 'ai-generated-label.png', { type: 'image/png' });
        generateBottlePreview(file);
      })
      .catch(err => {
        console.error('Error loading AI label for preview:', err);
      });
  };

  const getSourceBadge = (sourceType) => {
    const badges = {
      upload: { label: 'Caricata', color: 'bg-blue-100 text-blue-800' },
      ai_generated: { label: 'AI', color: 'bg-purple-100 text-purple-800' },
      canva: { label: 'Canva', color: 'bg-green-100 text-green-800' }
    };
    return badges[sourceType] || badges.upload;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Le Mie Etichette</h2>
          <p className="text-gray-600 mt-1">Gestisci le tue etichette personalizzate</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all font-medium"
        >
          <Plus className="w-5 h-5 mr-2" />
          Crea Nuova Etichetta
        </button>
      </div>

      {/* Labels Grid */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader className="w-8 h-8 text-blue-600 animate-spin mb-3" />
          <p className="text-sm text-gray-600">Caricamento...</p>
        </div>
      ) : labels.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <Palette className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nessuna etichetta ancora</h3>
          <p className="text-gray-600 mb-4">Inizia creando la tua prima etichetta personalizzata</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5 mr-2" />
            Crea Prima Etichetta
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {labels.map((label) => {
            const badge = getSourceBadge(label.sourceType);
            return (
              <div key={label.id} className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden hover:border-blue-400 hover:shadow-lg transition-all">
                {/* Thumbnail */}
                <div className="h-48 bg-gray-100 flex items-center justify-center overflow-hidden relative group">
                  <img 
                    src={getImageUrl(label.labelImagePath)} 
                    alt={label.name}
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform cursor-pointer"
                    onClick={() => setZoomedImage(getImageUrl(label.labelImagePath))}
                  />
                  <button
                    onClick={() => setZoomedImage(getImageUrl(label.labelImagePath))}
                    className="absolute top-2 right-2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-all opacity-0 group-hover:opacity-100"
                  >
                    <ZoomIn className="w-4 h-4 text-gray-700" />
                  </button>
                </div>

                {/* Content */}
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-gray-900 text-sm">{label.name}</h3>
                    <span className={`px-2 py-1 text-xs rounded ${badge.color}`}>
                      {badge.label}
                    </span>
                  </div>
                  
                  {label.description && (
                    <p className="text-xs text-gray-600 mb-3 line-clamp-2">{label.description}</p>
                  )}

                  <p className="text-xs text-gray-500 mb-3">
                    {new Date(label.createdAt).toLocaleDateString('it-IT')}
                  </p>

                  {/* Actions */}
                  <div className="flex gap-2">
                    {label.bottlePreviewPath && (
                      <button
                        onClick={() => setZoomedImage(getImageUrl(label.bottlePreviewPath))}
                        className="flex-1 flex items-center justify-center px-3 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors text-xs"
                        title="Vedi Preview 3D"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    )}
                    <a
                      href={getImageUrl(label.labelImagePath)}
                      download
                      className="flex-1 flex items-center justify-center px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors text-xs"
                      title="Scarica"
                    >
                      <Download className="w-4 h-4" />
                    </a>
                    <button
                      onClick={() => handleUseForOrder(label)}
                      className="flex-1 flex items-center justify-center px-3 py-2 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors text-xs"
                      title="Usa per ordine"
                    >
                      <ShoppingCart className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteLabel(label.id)}
                      className="px-3 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors text-xs"
                      title="Elimina"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Crea Nuova Etichetta</h2>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetCreateForm();
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left: Design Tab */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">Design Etichetta</h3>
                  
                  {/* Tabs */}
                  <div className="flex border-b border-gray-200 mb-4">
                    <button
                      onClick={() => setActiveTab('upload')}
                      className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === 'upload'
                          ? 'border-blue-600 text-blue-600'
                          : 'border-transparent text-gray-500'
                      }`}
                    >
                      📁 Carica
                    </button>
                    <button
                      onClick={() => setActiveTab('ai')}
                      className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === 'ai'
                          ? 'border-purple-600 text-purple-600'
                          : 'border-transparent text-gray-500'
                      }`}
                    >
                      ✨ AI
                    </button>
                    <button
                      onClick={() => setActiveTab('canva')}
                      className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === 'canva'
                          ? 'border-blue-600 text-blue-600'
                          : 'border-transparent text-gray-500'
                      }`}
                    >
                      🎨 Canva
                    </button>
                  </div>

                  {/* Tab Content */}
                  <div className="bg-gray-50 rounded-lg p-4 max-h-[400px] overflow-y-auto">
                    {activeTab === 'upload' && (
                      <CustomerLabelUpload 
                        onImageUploaded={(imageUrl, file) => {
                          setLabelImage(imageUrl);
                          if (file) {
                            generateBottlePreview(file);
                          }
                        }}
                        currentImage={labelImage}
                        skipUpload={true}
                      />
                    )}

                    {activeTab === 'ai' && (
                      <LabelGeneratorAI onLabelGenerated={handleAILabelGenerated} />
                    )}

                    {activeTab === 'canva' && (
                      <CanvaTemplateGallery />
                    )}
                  </div>
                </div>

                {/* Right: Preview & Save */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">Anteprima & Salva</h3>
                  
                  {/* Preview */}
                  <div className="bg-gray-100 rounded-lg p-6 mb-4 min-h-[300px] flex items-center justify-center">
                    {generatingPreview ? (
                      <div className="text-center">
                        <Loader className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
                        <p className="text-sm text-gray-600">Generazione preview 3D...</p>
                      </div>
                    ) : bottlePreview ? (
                      <img 
                        src={getImageUrl(bottlePreview)} 
                        alt="Preview 3D"
                        className="max-w-full max-h-[300px] object-contain rounded-lg shadow-xl cursor-pointer"
                        onClick={() => setZoomedImage(getImageUrl(bottlePreview))}
                      />
                    ) : labelImage ? (
                      <img 
                        src={getImageUrl(labelImage)} 
                        alt="Etichetta"
                        className="max-w-full max-h-[300px] object-contain rounded-lg shadow-xl"
                      />
                    ) : (
                      <div className="text-center text-gray-500">
                        <Palette className="w-16 h-16 mx-auto mb-3 text-gray-400" />
                        <p className="text-sm">Crea o carica un'etichetta</p>
                      </div>
                    )}
                  </div>

                  {/* Form */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nome Etichetta *
                      </label>
                      <input
                        type="text"
                        value={labelName}
                        onChange={(e) => setLabelName(e.target.value)}
                        placeholder="es: Amaro Elegante"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Descrizione (opzionale)
                      </label>
                      <textarea
                        value={labelDescription}
                        onChange={(e) => setLabelDescription(e.target.value)}
                        placeholder="Breve descrizione..."
                        rows="3"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    {/* Save Button */}
                    <button
                      onClick={handleSaveLabel}
                      disabled={saving || !labelImage || !labelName.trim()}
                      className="w-full flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium"
                    >
                      {saving ? (
                        <>
                          <Loader className="w-5 h-5 mr-2 animate-spin" />
                          Salvataggio...
                        </>
                      ) : (
                        <>
                          <Plus className="w-5 h-5 mr-2" />
                          Salva nella Libreria
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Zoom Modal */}
      {zoomedImage && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setZoomedImage(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <img 
              src={zoomedImage} 
              alt="Zoom" 
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={() => setZoomedImage(null)}
              className="absolute top-4 right-4 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg"
            >
              <X className="w-6 h-6 text-gray-700" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyLabels;
